import { Component, EventEmitter, Input, Output } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable } from 'rxjs';

import { ResourceEmployeeProfile } from '../../../../Models/resource-modules.models';
import { ResourceKnowledge } from '../../../../Models/resource.models';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../../shared/confirm-dialog/confirm-dialog.component';
import { ResourceTeamManagementStore } from '../resource-team-management.store';
import {
  ResourcePlanningMembersDialogComponent,
  ResourcePlanningMembersDialogData
} from '../planning-members-dialog/resource-planning-members-dialog.component';

/** Team-Filter: Team-ID, 'none' (ohne Team) oder null (alle). */
export type ResourceTeamFilter = number | 'none' | null;

/** Status-Filter: in der Gesamtplanung, nicht in der Planung oder alle Benutzerkonten. */
export type ResourcePlanningStatusFilter = 'planning' | 'none' | 'all';

/** Wie ein Mitarbeiter in der Gesamtplanung enthalten ist. */
export type ResourcePlanningSource = 'team' | 'manual' | 'both' | 'none';

/**
 * „Alle Mitarbeiter“: jeder Mitarbeiter genau einmal, mit Teams, Hauptteam,
 * Teamleitung, Gesamtplanung (automatisch über Team / manuell / nicht enthalten),
 * Skills und Wochenkapazität. Filter nach Name, Team (auch „ohne Team“), Skill und
 * Planungsstatus. Auswahl öffnet das Profil im Seitenpanel. Mitarbeiter können hier
 * mit wenigen Klicks manuell zur Gesamtplanung hinzugefügt oder daraus entfernt werden.
 */
@Component({
  selector: 'app-resource-employee-directory',
  templateUrl: './resource-employee-directory.component.html',
  styleUrls: ['../resource-team-management.shared.css', './resource-employee-directory.component.css']
})
export class ResourceEmployeeDirectoryComponent {
  @Input() selectedUserId: string | null = null;
  @Output() openProfile = new EventEmitter<string>();

  search = '';
  teamFilter: ResourceTeamFilter = null;
  skillFilter: number | null = null;
  /** Standard: Mitarbeiter der Gesamtplanung; „alle“ zeigt auch Benutzerkonten außerhalb der Planung. */
  statusFilter: ResourcePlanningStatusFilter = 'planning';

  constructor(
    public store: ResourceTeamManagementStore,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  get employees(): ResourceEmployeeProfile[] {
    const q = this.search.trim().toLocaleLowerCase('de');
    return this.store.profiles.filter(p =>
      (this.statusFilter === 'all' || (this.statusFilter === 'planning' ? p.isInPlanning : !p.isInPlanning)) &&
      (!q || p.name.toLocaleLowerCase('de').includes(q) || (p.email ?? '').toLocaleLowerCase('de').includes(q)) &&
      (this.teamFilter === null ||
        (this.teamFilter === 'none' ? p.memberships.length === 0 : p.memberships.some(m => m.teamId === this.teamFilter))) &&
      (this.skillFilter === null || p.skillIds.includes(this.skillFilter)));
  }

  get withoutTeamCount(): number {
    return this.store.profiles.filter(p => p.memberships.length === 0).length;
  }

  get inPlanningCount(): number {
    return this.store.profiles.filter(p => p.isInPlanning).length;
  }

  get notInPlanningCount(): number {
    return this.store.profiles.filter(p => !p.isInPlanning).length;
  }

  get manualCount(): number {
    return this.store.profiles.filter(p => p.isManuallyPlanned).length;
  }

  /** Teams im Filter: nur aktive Teams. */
  get filterTeams() {
    return this.store.teams.filter(t => t.isActive);
  }

  get filterSkills(): ResourceKnowledge[] {
    const used = new Set(this.store.profiles.flatMap(p => p.skillIds));
    return this.store.skills.filter(s => used.has(s.id));
  }

  get hasFilters(): boolean {
    return !!this.search || this.teamFilter !== null || this.skillFilter !== null;
  }

  primaryTeamName(profile: ResourceEmployeeProfile): string | null {
    return profile.memberships.find(m => m.teamId === profile.primaryTeamId)?.teamName ?? null;
  }

  skillsOf(profile: ResourceEmployeeProfile): ResourceKnowledge[] {
    return this.store.skills.filter(s => profile.skillIds.includes(s.id));
  }

  isLeader(profile: ResourceEmployeeProfile): boolean {
    return profile.memberships.some(m => m.isLeader);
  }

  /** Quelle der Gesamtplanung: Team, manuell, beides oder nicht enthalten. */
  planningSource(profile: ResourceEmployeeProfile): ResourcePlanningSource {
    if (profile.isPlannedByTeam && profile.isManuallyPlanned) return 'both';
    if (profile.isPlannedByTeam) return 'team';
    if (profile.isManuallyPlanned) return 'manual';
    return 'none';
  }

  resetFilters(): void {
    this.search = '';
    this.teamFilter = null;
    this.skillFilter = null;
  }

  trackProfile(_i: number, p: ResourceEmployeeProfile): string { return p.userId; }

  // ---- Gesamtplanung: manuell hinzufügen / entfernen ----------------------------------

  /** Dialog mit durchsuchbarer Auswahl bestehender Benutzerkonten (keine Kontoanlage). */
  openAddDialog(): void {
    if (!this.store.canManagePlanningMembers || this.store.busy) return;
    const data: ResourcePlanningMembersDialogData = {
      candidates: this.store.planningCandidates,
      profiles: this.store.profiles
    };
    this.dialog.open<ResourcePlanningMembersDialogComponent, ResourcePlanningMembersDialogData, string[]>(
      ResourcePlanningMembersDialogComponent, { data, width: '560px', maxWidth: '95vw', autoFocus: 'first-tabbable' })
      .afterClosed()
      .subscribe(userIds => {
        if (!userIds?.length) return;
        this.handle(this.store.addToPlanning(userIds),
          userIds.length === 1 ? 'Mitarbeiter zur Gesamtplanung hinzugefügt.' : `${userIds.length} Mitarbeiter zur Gesamtplanung hinzugefügt.`);
      });
  }

  /** Einen einzelnen Mitarbeiter direkt aus der Zeile manuell aufnehmen. */
  addToPlanning(profile: ResourceEmployeeProfile, event?: Event): void {
    event?.stopPropagation();
    if (!this.store.canManagePlanningMembers || profile.isManuallyPlanned || this.store.busy) return;
    this.handle(this.store.addToPlanning([profile.userId]), `${profile.name} ist jetzt in der Gesamtplanung.`);
  }

  /** Manuelle Aufnahme aufheben – nur das Kennzeichen; Daten und Teams bleiben. */
  removeFromPlanning(profile: ResourceEmployeeProfile, event?: Event): void {
    event?.stopPropagation();
    if (!this.store.canManagePlanningMembers || !profile.isManuallyPlanned || this.store.busy) return;
    const teamNames = profile.memberships.map(m => m.teamName);
    this.confirm({
      title: 'Aus der Gesamtplanung entfernen',
      message: `Manuelle Aufnahme von ${profile.name} in die Gesamtplanung aufheben?`,
      details: [
        'Benutzerkonto, Termine, Abwesenheiten und Kapazitätsdaten bleiben erhalten.',
        ...(teamNames.length
          ? [`Bleibt über ${teamNames.length === 1 ? 'das Team' : 'die Teams'} ${teamNames.join(', ')} weiterhin in der Gesamtplanung.`]
          : ['Ohne Team erscheint der Mitarbeiter danach nicht mehr in der Gesamtplanung.'])
      ],
      confirmText: 'Entfernen'
    }, () => this.handle(this.store.removeFromPlanning(profile.userId), 'Manuelle Aufnahme aufgehoben.'));
  }

  // ---- Hilfen ---------------------------------------------------------------------------

  private handle(request: Observable<unknown>, message: string): void {
    request.subscribe({
      next: () => this.snackBar.open(message, 'OK', { duration: 3000 }),
      error: (err: HttpErrorResponse) => this.snackBar.open(
        ResourceTeamManagementStore.errorText(err, 'Die Aktion ist fehlgeschlagen.'), 'OK', { duration: 5000 })
    });
  }

  private confirm(data: ConfirmDialogData, onConfirm: () => void): void {
    this.dialog.open(ConfirmDialogComponent, { data, width: '460px', maxWidth: '95vw' })
      .afterClosed()
      .subscribe(ok => { if (ok) onConfirm(); });
  }
}
